import React, { Component, Suspense, lazy } from 'react';
import { Route, Switch, Redirect, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import DashboardSidebar from './DashboardSidebar';
import DashboardBookings from './DashboardBookings';
import { BookingSheetRoute } from './BookingSheet';
import DashboardReviews from './DashboardReviews';
import DashboardAccount from './DashboardAccount';
import DashboardMarketingPreferences from './DashboardMarketingPreferences';
import WalletHub from '../wallet/WalletHub';
import PullToRefresh from '../common/PullToRefresh/PullToRefresh';
import { MESSAGES_VISIBLE } from '../../config/featureFlags';
import './dashboard.css';

// Messages with salons (Beu Messages, client side): loaded when first opened.
const ClientMessagesPage = lazy(() => import('../messages/client/ClientMessagesPage'));

// Back Arrow Icon
const BackArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
    <path d="M19 12H5M12 19l-7-7 7-7" />
  </svg>
);

// Tab groups in bottom-bar order. Routes in the same group (wallet/loyalty
// are one Wallet tab) share a key, so switching between them never replays
// the screen transition; crossing groups slides in from the travel direction.
const TAB_GROUPS = [
  ['/dashboard/home'],
  ['/dashboard/bookings'],
  ['/dashboard/messages'],
  ['/dashboard/wallet', '/dashboard/loyalty'],
  ['/dashboard/account', '/dashboard/reviews', '/dashboard/help', '/dashboard/marketing-preferences'],
];

const groupIndexFor = (pathname) => {
  const idx = TAB_GROUPS.findIndex((group) =>
    group.some((p) => pathname === p || pathname.startsWith(p + '/'))
  );
  return idx === -1 ? 0 : idx;
};

class ClientDashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isMobileMenuOpen: false
    };
    this.prevGroupIndex = groupIndexFor(props.location.pathname);
  }

  toggleMobileMenu = () => {
    this.setState(prevState => ({
      isMobileMenuOpen: !prevState.isMobileMenuOpen
    }));
  };

  closeMobileMenu = () => {
    this.setState({ isMobileMenuOpen: false });
  };

  render() {
    const { isMobileMenuOpen } = this.state;

    // Directional screen transition: entering a higher tab group slides in
    // from the right, a lower one from the left - matching the physical
    // direction of travel along the bottom bar. Same-group navigation (e.g.
    // the Wallet/Loyalty sub-tab swipe) keeps the same key: no remount, no
    // replayed animation.
    const groupIndex = groupIndexFor(this.props.location.pathname);
    const direction = groupIndex >= this.prevGroupIndex ? 'right' : 'left';
    this.prevGroupIndex = groupIndex;

    return (
      <div className="client-dashboard">
        {/* Sidebar Navigation - Desktop only */}
        <DashboardSidebar
          isMobileOpen={isMobileMenuOpen}
          onCloseMobile={this.closeMobileMenu}
        />

        {/* Main Content Area */}
        <div className="client-dashboard-content">
          {/* Main Content */}
          <main className="dashboard-main has-mobile-tab-bar">
            <div key={groupIndex} className={`dashboard-route-slide from-${direction}`}>
            <Switch>
              {/* The old home page was a placeholder (a fixed "No upcoming
                  bookings" and made-up activity): bookings is the home */}
              <Redirect exact from="/dashboard/home" to="/dashboard/bookings" />
              {/* Not exact: the list stays mounted under the booking sheet
                  (/dashboard/bookings/:id), which renders outside this main
                  so the route transition's transform cannot capture it. */}
              <Route path="/dashboard/bookings" render={(p) => <PullToRefresh><DashboardBookings {...p} /></PullToRefresh>} />
              {MESSAGES_VISIBLE && (
                <Route exact path="/dashboard/messages/:chatId?" render={(p) => (
                  <Suspense fallback={null}><ClientMessagesPage {...p} /></Suspense>
                )} />
              )}
              <Route exact path="/dashboard/reviews" component={DashboardReviews} />
              <Route exact path="/dashboard/wallet" render={(p) => <PullToRefresh><WalletHub {...p} /></PullToRefresh>} />
              <Route exact path="/dashboard/loyalty" render={(p) => <PullToRefresh><WalletHub {...p} /></PullToRefresh>} />
              <Route exact path="/dashboard/account" component={DashboardAccount} />
              <Route exact path="/dashboard/marketing-preferences" component={DashboardMarketingPreferences} />
              
              <Route exact path="/dashboard/help" render={() => (
                <div className="dashboard-fade-in">
                  <div className="dashboard-page-header">
                    <button className="dashboard-back-button" onClick={() => this.props.history.push('/dashboard/account')}>
                      <BackArrowIcon />
                    </button>
                    <div className="dashboard-page-header-text">
                      <h1 className="dashboard-page-title">Help & Support</h1>
                      <p className="dashboard-page-subtitle">Get assistance with your bookings</p>
                    </div>
                  </div>
                  {/* Real ways to get help, not a "coming soon" dead end */}
                  <div className="dashboard-card dashboard-help">
                    <h2 className="dashboard-card-title">About a booking</h2>
                    <p className="dashboard-help-text">To move, cancel or ask about an appointment, open it in Bookings. Each booking has the salon's phone number and email.</p>
                    <button type="button" className="dashboard-btn dashboard-btn-secondary" onClick={() => this.props.history.push('/dashboard/bookings')}>Go to my bookings</button>
                  </div>
                  <div className="dashboard-card dashboard-help">
                    <h2 className="dashboard-card-title">Anything else</h2>
                    <p className="dashboard-help-text">Questions about your account, payments or the app: email us and we'll get back to you.</p>
                    <a className="dashboard-btn dashboard-btn-primary" href="mailto:info@beuapp.com?subject=Help%20with%20my%20Beu%20account">Email info@beuapp.com</a>
                  </div>
                </div>
              )} />
              
              {/* Default: their bookings */}
              <Redirect from="/dashboard" to="/dashboard/bookings" />
            </Switch>
            </div>
          </main>
        </div>
        {/* One booking as a sheet over the list: photo first, every service,
            policy, reschedule and cancel. Deep-linkable. */}
        <BookingSheetRoute />
      </div>
    );
  }
}

function mapStateToProps(state) {
  const { authentication } = state;
  return {
    user: authentication?.user || null
  };
}

export default withRouter(connect(mapStateToProps)(ClientDashboard));